import { defineComponent as _defineComponent } from 'vue'
import { ComponentPublicInstance, computed, ComputedRef, defineAsyncComponent, Ref, ref } from 'vue';

import { PopupMenuApi } from '@/components/dialogs/popupmenu/PopupMenu.vue';
import { nonCircularCopy, PopupMenuItem } from '@/components/dialogs/popupmenu/PopupMenuItem';
import BrandIcon from '@/components/icons/BrandIcon.vue';
import { useRouter } from '@/composables/router/VueRouter';
import BrowserDetection from '@/data/BrowserDetection';

interface Props {
  value: PopupMenuItem;
  name?: string;
  icon?: string;
  // Flag for option to show focus indication around icons as per BrandIcon.vue
  showIconFocus?: boolean;
  disabled?: boolean;
  isSubMenu?: boolean;
  isRoot?: boolean;
  showChevron?: boolean;
  acceptFocus?: boolean;
  // Flag for option to display menu items horizontally as opposed to the default vertical
  horizontal?: boolean;
  parentValue?: PopupMenuItem;
}
interface Emits {
  (e: 'change', item: PopupMenuItem): void,
  (e: 'itemClicked', value: PopupMenuItem): void,
  (e: 'subMenuToggle'): void,
  (e: 'subMenuOpen'): void,
  (e: 'subMenuClose', closeAll: boolean): void,
}
export interface PopupMenuButtonApi extends ComponentPublicInstance {
  focus: () => void;
  blur: () => void;
  getValue: () => PopupMenuItem;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'PopupMenuButton',
  props: {
    value: null,
    name: null,
    icon: null,
    showIconFocus: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    isSubMenu: { type: Boolean, default: false },
    isRoot: { type: Boolean, default: false },
    showChevron: { type: Boolean, default: false },
    acceptFocus: { type: Boolean, default: true },
    horizontal: { type: Boolean, default: false },
    parentValue: null
  },
  emits: ["change", "itemClicked", "subMenuToggle", "subMenuOpen", "subMenuClose"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'change', item: PopupMenuItem): void,
  (e: 'itemClicked', value: PopupMenuItem): void,
  (e: 'subMenuToggle'): void,
  (e: 'subMenuOpen'): void,
  (e: 'subMenuClose', closeAll: boolean): void,
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: PopupMenuItem, name?: string, icon?: string, showIconFocus: boolean, disabled: boolean, isSubMenu: boolean, isRoot: boolean, showChevron: boolean, acceptFocus: boolean, horizontal: boolean, parentValue?: PopupMenuItem }

const PopupMenu = defineAsyncComponent(() => import('@/components/dialogs/popupmenu/PopupMenu.vue'));

const router = useRouter();





const button: Ref<HTMLButtonElement | null> = ref(null);
const subMenu: Ref<PopupMenuApi | null> = ref(null);

function emitItemClicked(): PopupMenuItem {
  emitSubMenuClose();
  emit('itemClicked', props.value);
  return props.value;
}

function getValue(): PopupMenuItem {
  return props.value;
}

const subMenuValue: ComputedRef<PopupMenuItem | undefined> = computed(() => {
  return nonCircularCopy(props.value);
});

function focus(): void {
  button.value?.focus();
}

function blur(): void {
  button.value?.blur();
}

function emitSubMenuToggle(): void {
  // Toggled sub menu
  emit('subMenuToggle');
}

function emitSubMenuOpen(): void {
  // Opened sub menu
  emit('subMenuOpen');
}

function emitSubMenuClose(closeAll: boolean = true): boolean {
  // Closed sub menu
  emit('subMenuClose', closeAll);
  return closeAll;
}

const ariaLabel: ComputedRef<string | null> = computed(() => {
  return props.acceptFocus && props.value.name ? props.value.name : null;
});

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

function onSubMenuClose(closeAll: boolean = true): void {
  if (closeAll) {
    emitSubMenuClose();
  }
}

function onKeyPress(event: KeyboardEvent): void {
  if (props.value && !props.value.menuItems) {
    if (!props.horizontal) {
      switch (event.key) {
        case 'ArrowLeft' :
          event.stopImmediatePropagation();
          event.preventDefault();
          emitSubMenuClose(false);
          break;
        case 'ArrowRight' :
          event.stopImmediatePropagation();
          event.preventDefault();
          emitSubMenuOpen();
          break;
        case ' ' :
          event.stopImmediatePropagation();
          event.preventDefault();
          emitSubMenuToggle();
          break;
        case 'Enter' :
          event.stopImmediatePropagation();
          event.preventDefault();
          clicked();
          break;
      }
    } else {
      switch (event.key) {
        case 'ArrowDown' :
          event.stopImmediatePropagation();
          event.preventDefault();
          emitSubMenuClose(false);
          break;
        case 'ArrowUp' :
          event.stopImmediatePropagation();
          event.preventDefault();
          emitSubMenuOpen();
          break;
        case ' ' :
          event.stopImmediatePropagation();
          event.preventDefault();
          emitSubMenuToggle();
          break;
        case 'Enter' :
          event.stopImmediatePropagation();
          event.preventDefault();
          clicked();
          break;
      }
    }
  }
}

function onFocus(): void {
  subMenu.value?.focusMenuOpenerButton();
}

function clicked(): void {
  if (!props.disabled && props.value) {
    switch (props.value.itemType) {
      case 'item_menu' :
        if (props.isRoot) {
          emitSubMenuToggle();
        } else {
          emitSubMenuOpen();
        }
        break;
      case 'item' :
      case 'item_hybrid' :
        if (props.isRoot) {
          emitSubMenuToggle();
        } else {
          if (props.value.route) {
            router.push(props.value.route);
          } else if (props.value.callback) {
            props.value.callback(props.value);
          }
          emitItemClicked();
        }
        break;
      case 'menu' :
        if (props.value.route) {
          router.push(props.value.route);
        } else if (props.value.callback) {
          props.value.callback(props.value);
        } else {
          emitSubMenuToggle();
        }
        break;
      case 'url' :
        window.open(props.value.url, '_blank');
        emitSubMenuClose();
        break;
      case 'action' :
        if (props.value.route) {
          router.push(props.value.route);
        } else if (props.value.callback) {
          props.value.callback(props.parentValue);
        }
        emitSubMenuClose();
        break;
      case 'in_menu_action' :
        if (props.value.callback) {
          props.value.callback(props.parentValue);
        }
        break;
    }
  } else {
    emitItemClicked();
  }
}

function itemSelected(item: PopupMenuItem): void {
  emit('change', item);
}

expose({
  focus,
  blur,
  getValue,
});

return { __sfc: true,PopupMenu, router, props, emit, button, subMenu, emitItemClicked, getValue, subMenuValue, focus, blur, emitSubMenuToggle, emitSubMenuOpen, emitSubMenuClose, ariaLabel, isMobile, onSubMenuClose, onKeyPress, onFocus, clicked, itemSelected, BrandIcon }
}

})